import { cloneElement, isValidElement, useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');
export { cx };

export function Card({ children, className, tone }: { children: ReactNode; className?: string; tone?: 'warn' | 'down' | 'accent' | 'live' | 'up' }) {
  const border = { warn: 'border-[#5a4316]', down: 'border-[#6a2a27]', accent: 'border-[#22385a]', live: 'border-[#2e2650]', up: 'border-[#1e5a3c]' }[tone ?? 'x' as never] ?? 'border-line';
  return <div className={cx('rounded-xl border bg-panel', border, className)}>{children}</div>;
}

export function CardHeader({ title, right, id }: { title: ReactNode; right?: ReactNode; id?: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
      <h2 id={id} className="m-0 text-[15px] font-semibold">
        {title}
      </h2>
      {right}
    </div>
  );
}

type PillTone = 'up' | 'down' | 'warn' | 'accent' | 'live' | 'neutral';
export function Pill({ tone = 'neutral', children, className }: { tone?: PillTone; children: ReactNode; className?: string }) {
  const t = { up: 'bg-up-bg text-up', down: 'bg-down-bg text-down', warn: 'bg-warn-bg text-warn', accent: 'bg-accent-bg text-accent', live: 'bg-[#221b3d] text-live', neutral: 'bg-raised text-sub border border-line2' }[tone];
  return <span className={cx('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap', t, className)}>{children}</span>;
}

export function Label({ children }: { children: ReactNode }) {
  return <span className="text-xs uppercase tracking-[0.06em] text-muted">{children}</span>;
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
export function Button({ variant = 'secondary', className, ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  const v = {
    primary: 'bg-accent text-bg font-bold border-0',
    secondary: 'bg-[#1a1f27] text-ink border border-[#2b3340] font-semibold',
    ghost: 'bg-transparent text-sub border border-line2',
    danger: 'bg-transparent text-down border border-[#6a2a27] font-semibold',
    success: 'bg-up text-bg font-bold border-0',
  }[variant];
  return <button type="button" {...p} className={cx('min-h-10 cursor-pointer rounded-lg px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50', v, className)} />;
}

const fieldCls = 'w-full rounded-lg border border-line2 bg-field px-3 py-2.5 text-ink placeholder:text-muted';

/** Labelled form control. The hint sits outside the label (so it isn't part of the field's name) and is linked with aria-describedby. */
export function Field({ label, hint, children, className }: { label: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  const hintId = useId();
  const control = hint && isValidElement<{ 'aria-describedby'?: string }>(children) ? cloneElement(children, { 'aria-describedby': hintId }) : children;
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs text-muted">{label}</span>
        {control}
      </label>
      {hint && <span id={hintId} className="text-xs text-muted">{hint}</span>}
    </div>
  );
}

export const Input = ({ className, mono, ...p }: InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }) => <input {...p} className={cx(fieldCls, mono && 'text-right font-mono', className)} />;
export const Select = ({ className, ...p }: SelectHTMLAttributes<HTMLSelectElement>) => <select {...p} className={cx(fieldCls, className)} />;
export const Textarea = ({ className, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea rows={3} {...p} className={cx(fieldCls, 'resize-y leading-relaxed', className)} />;

export function Segmented<T extends string>({ options, value, onChange, label, colors }: { options: { value: T; label: string }[]; value: T | null | undefined; onChange: (v: T) => void; label: string; colors?: Partial<Record<T, string>> }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={cx('min-h-10 cursor-pointer rounded-lg px-3.5 py-2 text-sm', on ? 'border-0 font-bold text-bg' : 'border border-line2 bg-raised text-sub')}
            style={on ? { background: colors?.[o.value] ?? '#e7ebf0' } : undefined}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="m-0 rounded-lg border border-[#6a2a27] bg-down-bg px-3 py-2 text-sm text-down">
      {error instanceof Error ? error.message : String(error)}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="m-0 px-4 py-6 text-center text-sub">{children}</p>;
}

export function Stat({ label, value, note, tone }: { label: string; value: ReactNode; note?: ReactNode; tone?: string }) {
  return (
    <Card className="flex flex-col gap-1.5 p-3.5">
      <span className="text-xs text-muted">{label}</span>
      <span className={cx('font-mono text-xl font-semibold', tone)}>{value}</span>
      {note && <span className="text-xs text-sub">{note}</span>}
    </Card>
  );
}

export function Bar({ pct, color = '#6aa8ff' }: { pct: number; color?: string }) {
  return (
    <div className="h-1.5 rounded bg-[#232a34]">
      <div className="h-1.5 rounded" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: color }} />
    </div>
  );
}

export function PageHeader({ title, sub, right, back }: { title: ReactNode; sub?: ReactNode; right?: ReactNode; back?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-col gap-1">
        {back}
        <h1 className="m-0 text-2xl font-bold tracking-tight">{title}</h1>
        {sub && <span className="text-sub">{sub}</span>}
      </div>
      {right && <div className="flex flex-wrap items-center gap-2.5">{right}</div>}
    </header>
  );
}
